import { createStore, applyMiddleware, compose } from 'redux';
import { persistStore, persistReducer, createTransform } from 'redux-persist';
import { routerMiddleware, connectRouter } from 'connected-react-router';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web
import createSagaMiddleware from 'redux-saga';
import rootSaga from './rootSaga';
import rootReducer from './rootReducer';
import { history } from './Utils/utils';
import immutablePersistenceTransform from './Utils/ImmutablePersistenceTransform';
import { setReduxStore } from './Containers/LoginPage/saga';
import { INITIAL_STATE as DASHBOARD_INITIAL_STATE } from './Containers/DashboardPage/reducer';

const R = require('ramda');

// Persist only the user's selections from `dashboard`. Event rows, chart data,
// search text and absolute periods hold customer data (IPs, emails) and go stale
// against the site's retention window, so they are refetched on every load.
const DASHBOARD_PERSISTED_KEYS = ['apiKeySelected', 'terms_accepted'];
const dashboardSafeKeysTransform = createTransform(
  (inbound) => R.pick(DASHBOARD_PERSISTED_KEYS, inbound),
  // autoMergeLevel1 replaces the whole slice, so rebuild it on INITIAL_STATE.
  (outbound) => DASHBOARD_INITIAL_STATE.merge(outbound),
  { whitelist: ['dashboard'] },
);

const persistConfig = {
  key: 'deduce_security',
  reducerVersion: '0.23',
  storage,
  // Report slices only hold fetched data, so nothing of theirs is persisted.
  blacklist: [
    'router',
    'errors',
    'user',
    'report',
    'monthlyReport',
    'monthlyScoreReport',
    'balancerMetrics',
  ],
  // Optionally, just specify the keys you DO want stored to persistence.
  // An empty array means 'don't store any reducers' -> infinitered/ignite#409
  // whitelist: [],
  // Order matters: inbound runs left to right (pick keys, then Immutable to JS),
  // outbound right to left (JS to Immutable, then merge into INITIAL_STATE).
  transforms: [dashboardSafeKeysTransform, immutablePersistenceTransform],
};

export default async () => {
  const composeEnhancers =
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

  const sagaMiddleware = createSagaMiddleware();
  const initialState = {};

  const middleware = [sagaMiddleware, routerMiddleware(history)];

  const connectedRouterReducer = connectRouter(history)(rootReducer);
  const persistedReducer = persistReducer(
    persistConfig,
    connectedRouterReducer,
  );

  const store = createStore(
    persistedReducer,
    initialState,
    composeEnhancers(applyMiddleware(...middleware)),
  );

  setReduxStore(store);

  // Drop state persisted by an older reducerVersion before rehydrating. Purging
  // after persistStore() is too late: the old state is already in memory and the
  // next action writes it back.
  const storedVersion = await storage.getItem('reducerVersion');
  if (storedVersion !== persistConfig.reducerVersion) {
    await storage.removeItem(`persist:${persistConfig.key}`);
    await storage.setItem('reducerVersion', persistConfig.reducerVersion);
  }

  const persistor = persistStore(store);

  sagaMiddleware.run(rootSaga);

  return {
    store,
    persistor,
  };
};
